import React, { useState, useMemo } from 'react';
import { Box, Typography, Collapse, Tooltip } from '@mui/material';

interface IndexingDocument {
  input_id: string;
  input_original_filename: string;
  input_classifier_rename: string;
  step_kb_index_status: string;
  step_kb_index_errors: string[];
}

interface IndexingStatusBannerProps {
  indexingStatus: {
    all_documents_indexed: boolean;
    indexed_documents: number;
    total_documents: number;
    pending_documents: number;
    failed_documents: number;
    documents: IndexingDocument[];
  };
}

const INDEXED_STATUSES = ['success', 'warning'];

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  error:                    { label: 'Erreur',              color: '#d32f2f' },
  timeout:                  { label: 'Timeout',             color: '#d32f2f' },
  not_requested:            { label: 'Non demandé',         color: '#9E9E9E' },
  requested:                { label: 'En attente',          color: '#ED6C02' },
  pdf_transform_in_progress:{ label: 'Traitement PDF…',    color: '#ED6C02' },
  pdf_transform_success:    { label: 'PDF traité',          color: '#ED6C02' },
  index_in_progress:        { label: 'Indexation…',         color: '#ED6C02' },
  in_progress:              { label: 'En cours…',           color: '#ED6C02' },
  retry:                    { label: 'Nouvelle tentative',  color: '#ED6C02' },
};

const getStatusDisplay = (status: string) =>
  STATUS_LABEL[status] ?? { label: status, color: '#9E9E9E' };

export const IndexingStatusBanner: React.FC<IndexingStatusBannerProps> = ({ indexingStatus }) => {
  const [showNonIndexedList, setShowNonIndexedList] = useState(false);

  const nonIndexedDocuments = useMemo(
    () => indexingStatus.documents.filter((doc) => !INDEXED_STATUSES.includes(doc.step_kb_index_status)),
    [indexingStatus.documents]
  );

  const hasNonIndexedDocs = nonIndexedDocuments.length > 0;

  if (indexingStatus.all_documents_indexed && !hasNonIndexedDocs) {
    return (
      <Box
        sx={{
          backgroundColor: '#e8f5e9',
          borderBottom: '1px solid #E4E7EC',
          padding: '12px 16px',
        }}
      >
        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#2e7d32' }}>
          Tous les documents ont été indexés.
        </Typography>
      </Box>
    );
  }

  if (indexingStatus.all_documents_indexed) return null;

  return (
    <Box
      sx={{
        backgroundColor: indexingStatus.failed_documents > 0 ? '#fff4e5' : '#e3f2fd',
        borderBottom: '1px solid #E4E7EC',
        padding: '12px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}
    >
      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
        {indexingStatus.pending_documents > 0 && 'Indexation en cours...'}
        {indexingStatus.failed_documents > 0 &&
          "Attention : Certains documents n'ont pas pu être indexés"}
      </Typography>

      <Typography component="span" sx={{ fontSize: '12px', color: '#6B7280' }}>
        Documents indexés : {indexingStatus.indexed_documents} / {indexingStatus.total_documents}
        {indexingStatus.pending_documents > 0 &&
          ` (${indexingStatus.pending_documents} en attente)`}
        {hasNonIndexedDocs && (
          <>
            {' ('}
            <Typography
              component="span"
              onClick={() => setShowNonIndexedList((prev) => !prev)}
              sx={{
                fontSize: '12px',
                color: '#d32f2f',
                textDecoration: 'underline',
                cursor: 'pointer',
                '&:hover': { color: '#b71c1c' },
              }}
            >
              {nonIndexedDocuments.length} non indexé{nonIndexedDocuments.length > 1 ? 's' : ''}
            </Typography>
            {')'}
          </>
        )}
      </Typography>

      <Collapse in={showNonIndexedList} timeout="auto" unmountOnExit>
        <Box
          sx={{
            mt: '6px',
            pl: '4px',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            maxHeight: '120px',
            overflowY: 'auto',
          }}
        >
          {nonIndexedDocuments.map((doc) => {
            const { label, color } = getStatusDisplay(doc.step_kb_index_status);
            const errors = doc.step_kb_index_errors ?? [];
            const statusLabel = (
              <Typography
                sx={{
                  fontSize: '11px',
                  color,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  cursor: errors.length > 0 ? 'help' : 'default',
                  textDecoration: errors.length > 0 ? 'underline dotted' : 'none',
                }}
              >
                {label}
              </Typography>
            );
            return (
              <Box
                key={doc.input_id}
                sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}
              >
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: '#374151',
                    lineHeight: 1.5,
                    '&::before': { content: '"- "' },
                    wordBreak: 'break-word',
                    flex: 1,
                  }}
                >
                  {doc.input_classifier_rename || doc.input_original_filename || doc.input_id}
                </Typography>
                {errors.length > 0 ? (
                  <Tooltip
                    title={
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {errors.map((err, i) => (
                          <Typography key={i} sx={{ fontSize: '11px' }}>
                            {err}
                          </Typography>
                        ))}
                      </Box>
                    }
                    placement="top"
                    arrow
                  >
                    {statusLabel}
                  </Tooltip>
                ) : statusLabel}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};
